import React, { useState, useMemo, useCallback, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import { FaChevronDown } from "react-icons/fa";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import "../../../styles/typography.css";
import "./StaffRequestList.css";
import {
  getRequestedListData,
  getStatusData,
  getStatesData,
  getSpeciesData,
  getProductTypeData,
  exportReport,
  downloadLabAnalysisReport,
} from "./StaffRequestListApi";
import { formatDate } from "../../../utility/Formatdate";
import { Container, Nav } from "react-bootstrap";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

function getLocalDateString(date) {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().split("T")[0];
}

const initialAdvancedSearch = {
  groupId: "",
  companyName: "",
  userName: "",
  salesRep: "",
  customerSampleId: "",
  lotNumber: "",
  sampleId: "",
  productType: "All",
  speciesType: "All",
  state: "All",
};

const StaffRequestList = () => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [fromDate, setFromDate] = useState(getLocalDateString(oneMonthAgo));
  const [toDate, setToDate] = useState(getLocalDateString(today));
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [selectedSampleIds, setSelectedSampleIds] = useState(new Set());
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [requestedListData, setRequestedListData] = useState([]);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [stateOptions, setStateOptions] = useState(["All"]);
  const [speciesOptions, setSpeciesOptions] = useState([
    { value: "All", label: "All" },
  ]);
  const [productTypeOptions, setProductTypeOptions] = useState([
    { value: "All", label: "All" },
  ]);
  const [advancedSearch, setAdvancedSearch] = useState(initialAdvancedSearch);
  const [totalRecords, setTotalRecords] = useState(0);
  const [allExpanded, setAllExpanded] = useState(false);
  const [paginationData, setPaginationData] = useState({});

  const collectSampleIds = (groupIds) => {
    const sampleIds = [];
    groupIds.forEach((groupId) => {
      const group = requestedListData.find((item) => item.groupID === groupId);
      if (
        group &&
        group.requestedSamples &&
        group.requestedSamples.length > 0
      ) {
        sampleIds.push(
          ...group.requestedSamples
            .map((sample) => sample.sampleID || sample.sampleId)
            .filter((id) => id !== null && id !== undefined)
        );
      }
    });
    return sampleIds;
  };

  const handleExport = async () => {
    try {
      if (selectedRowIds.size === 0 && selectedSampleIds.size === 0) {
        alert("Please select at least one row or sample to export");
        return;
      }
      
      if (selectedSampleIds.size > 0) {
        await exportReport([], Array.from(selectedSampleIds));
      } else {
        const groupIds = Array.from(selectedRowIds).filter(id => id !== null && id !== undefined);
        await exportReport(groupIds, []);
      }
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Failed to export report: ${error.message}`);
    }
  };

  const handleDownload = async () => {
    try {
      if (selectedRowIds.size === 0 && selectedSampleIds.size === 0) {
        alert("Please select at least one row or sample to download");
        return;
      }
      const selectedSamples = Array.from(selectedSampleIds);
      if (selectedSamples.length > 0) {
        await downloadLabAnalysisReport(selectedSamples);
      } else {
        const groupIds = Array.from(selectedRowIds);
        const sampleIds = collectSampleIds(groupIds);
        if (sampleIds.length === 0) {
          alert("No samples found in the selected rows");
          return;
        }
        await downloadLabAnalysisReport(sampleIds);
      }
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download lab analysis report. Please try again.");
    }
  };

  const tabConfig = [{ label: "Request List", key: "Request List" }];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await getStatusData();
        if (Array.isArray(res) && res.length > 0) {
          setStatusOptions(["All", ...res]);
        }
      } catch {
        setStatusOptions(["All"]);
      }
    };
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    const fetchStateOptions = async () => {
      try {
        const res = await getStatesData();
        if (Array.isArray(res) && res.length > 0) {
          setStateOptions(["All", ...res.map((s) => s.stateName)]);
        }
      } catch {
        setStateOptions(["All"]);
      }
    };
    fetchStateOptions();
  }, []);

  useEffect(() => {
    const fetchSpeciesOptions = async () => {
      try {
        const res = await getSpeciesData();
        if (Array.isArray(res) && res.length > 0) {
          setSpeciesOptions([
            { value: "All", label: "All" },
            ...res.map((s) => ({ value: s.speciesId, label: s.name })),
          ]);
        }
      } catch {
        setSpeciesOptions([{ value: "All", label: "All" }]);
      }
    };
    fetchSpeciesOptions();
  }, []);

  useEffect(() => {
    const fetchProductTypeOptions = async () => {
      try {
        const res = await getProductTypeData();
        if (Array.isArray(res) && res.length > 0) {
          setProductTypeOptions([
            { value: "All", label: "All" },
            ...res.map((p) => ({ value: p.productTypeId, label: p.name })),
          ]);
        }
      } catch {
        setProductTypeOptions([{ value: "All", label: "All" }]);
      }
    };
    fetchProductTypeOptions();
  }, []);

  const handleAdvancedInputChange = (e) => {
    const { name, value } = e.target;
    setAdvancedSearch((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async (search, currentPage = 1, rowsPerPage = 25) => {
    try {
      const basePayload = {
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        filterText: searchTerm,
        fromDate: fromDate ? new Date(fromDate).toISOString() : "",
        toDate: toDate ? new Date(toDate).toISOString() : "",
        status: statusFilter === "All" ? "" : statusFilter,
        groupId: 0,
        companyName: "",
        userName: "",
        salesRep: 0,
        customerSampleId: "",
        lotNumber: "",
        sampleId: 0,
        productType: 0,
        speciesType: 0,
        state: "",
      };
      const payload = {
        filterText: "",
        fromDate: fromDate ? new Date(fromDate).toISOString() : "",
        toDate: toDate ? new Date(toDate).toISOString() : "",
        status: statusFilter === "All" ? "" : statusFilter,
        groupId: advancedSearch.groupId ? Number(advancedSearch.groupId) : 0,
        companyName: advancedSearch.companyName || "",
        userName: advancedSearch.userName || "",
        salesRep: advancedSearch.salesRep ? Number(advancedSearch.salesRep) : 0,
        customerSampleId: advancedSearch.customerSampleId || "",
        lotNumber: advancedSearch.lotNumber || "",
        sampleId: advancedSearch.sampleId ? Number(advancedSearch.sampleId) : 0,
        productType:
          advancedSearch.productType && advancedSearch.productType !== "All"
            ? Number(advancedSearch.productType)
            : 0,
        speciesType:
          advancedSearch.speciesType && advancedSearch.speciesType !== "All"
            ? Number(advancedSearch.speciesType)
            : 0,
        state:
          advancedSearch.state && advancedSearch.state !== "All"
            ? advancedSearch.state
            : "",
      };

      const finalPayload = {
        ...basePayload,
        ...payload,
        pageNumber: currentPage,
        pageSize: rowsPerPage,
      };

      const response = await getRequestedListData(finalPayload);

      if (response && response.results) {
        const { totalRecords, pageNumber, pageSize, totalPages } = response;
        setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
        setRequestedListData(response.results);
        setTotalRecords(response.totalRecords || 0);
      } else {
        setRequestedListData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setRequestedListData([]);
      setTotalRecords(0);
    }
  };

  const toggleExpand = (groupId) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getCurrentPageData = useCallback(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return requestedListData.slice(start, end);
  }, [currentPage, rowsPerPage, requestedListData]);

  const handleExpandCollapseAll = useCallback(() => {
    if (allExpanded) {
      setExpandedIds(new Set());
      setAllExpanded(false);
    } else {
      const currentPageGroupIds = new Set(
        getCurrentPageData().map((item) => item.groupID)
      );
      setExpandedIds(currentPageGroupIds);
      setAllExpanded(true);
    }
  }, [allExpanded, getCurrentPageData]);

  useEffect(() => {
    const currentData = getCurrentPageData();
    if (currentData.length > 0) {
      const allGroupIds = currentData.map((item) => item.groupID);
      const allAreExpanded = allGroupIds.every((id) => expandedIds.has(id));
      setAllExpanded(allAreExpanded);
    }
  }, [expandedIds, getCurrentPageData]);

  const isAllSelectedOnPage = useMemo(() => {
    const currentData = getCurrentPageData();
    return (
      currentData.length > 0 &&
      currentData.every((r) => selectedRowIds.has(r.groupID))
    );
  }, [getCurrentPageData, selectedRowIds]);

  const toggleRowSelection = useCallback((groupID) => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(groupID)) copy.delete(groupID);
      else copy.add(groupID);
      return copy;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    const currentData = getCurrentPageData();
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (!isAllSelectedOnPage) {
        currentData.forEach((r) => copy.add(r.groupID));
      } else {
        currentData.forEach((r) => copy.delete(r.groupID));
      }
      return copy;
    });
  }, [getCurrentPageData, isAllSelectedOnPage]);

  const toggleSampleSelection = useCallback((sampleId) => {
    setSelectedSampleIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(sampleId)) {
        copy.delete(sampleId);
      } else {
        copy.add(sampleId);
      }
      return copy;
    });
  }, []);

  const areAllSamplesSelected = (samples) => {
    if (!samples || samples.length === 0) return false;
    return samples.every((sample) =>
      selectedSampleIds.has(sample.sampleID || sample.sampleId)
    );
  };

  const toggleAllSamplesSelection = (samples) => {
    if (!samples || samples.length === 0) return;

    const allSelected = areAllSamplesSelected(samples);
    setSelectedSampleIds((prev) => {
      const copy = new Set(prev);
      if (allSelected) {
        samples.forEach((sample) =>
          copy.delete(sample.sampleID || sample.sampleId)
        );
      } else {
        samples.forEach((sample) =>
          copy.add(sample.sampleID || sample.sampleId)
        );
      }
      return copy;
    });
  };

  const columns = useMemo(
    () => [
      {
        header: (
          <Form.Check
            type="checkbox"
            checked={isAllSelectedOnPage}
            onChange={toggleSelectAllOnPage}
            aria-label="Select all"
          />
        ),
        accessor: "checkbox",
        disableSort: true,
        cell: (row) => (
          <Form.Check
            type="checkbox"
            checked={selectedRowIds.has(row.groupID)}
            onChange={() => toggleRowSelection(row.groupID)}
            aria-label={`Select ${row.groupID}`}
          />
        ),
      },
      { header: "Company", accessor: "companyName" },
      { header: "Location", accessor: "locationName" },
      { header: "Group ID", accessor: "groupID" },
      { header: "User Name", accessor: "userName" },
      {
        header: (
          <div className="d-flex justify-content-end">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleExpandCollapseAll}
              className="expand-collapse-btn"
            >
              {allExpanded ? "Collapse All" : "Expand All"}
            </Button>
          </div>
        ),
        accessor: "expandControl",
        disableSort: true,
        cell: (row) => (
          <div className="d-flex justify-content-center">
            <div
              onClick={() => toggleExpand(row.groupID)}
              className="expand-icon"
              style={{ cursor: "pointer" }}
              aria-label={
                expandedIds.has(row.groupID)
                  ? "Collapse details"
                  : "Expand details"
              }
            >
              <FaChevronDown
                size={12}
                style={{
                  transform: expandedIds.has(row.groupID) ? "rotate(0deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </div>
          </div>
        ),
      },
    ],
    [
      isAllSelectedOnPage,
      selectedRowIds,
      toggleRowSelection,
      toggleSelectAllOnPage,
      allExpanded,
      expandedIds,
      handleExpandCollapseAll,
    ]
  );

  const renderExpandedPanel = (data) => {
    const subRowData = data.requestedSamples || [];
    const allSamplesSelected = areAllSamplesSelected(subRowData);

    return (
      <tr className="expanded-row" key={`${data.groupID}-expanded`}>
        <td colSpan={6} className="expanded-cell">
          <div className="expanded-content">
            <div className="expanded-table-container">
              <table className="expanded-table">
                <thead>
                  <tr className="expanded-table-header">
                    <th>
                      <Form.Check
                        type="checkbox"
                        checked={allSamplesSelected}
                        onChange={() => toggleAllSamplesSelection(subRowData)}
                        aria-label="Select all samples"
                      />
                    </th>
                    <th>Sample ID</th>
                    <th>Request Date</th>
                    <th>Received Date</th>
                    <th>Complete Date</th>
                    <th>Barcode No.</th>
                    <th>Species</th>
                    <th>Product</th>
                    <th>Lot No.</th>
                    <th>Exp Level</th>
                    <th>Range</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(subRowData || []).map((row, index) => (
                    <tr
                      key={row.sampleID || row.sampleId || index}
                      className={index % 2 === 0 ? "even-row" : "odd-row"}
                    >
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedSampleIds.has(row.sampleID || row.sampleId)}
                          onChange={() =>
                            toggleSampleSelection(row.sampleID || row.sampleId)
                          }
                          aria-label={`Select sample ${row.sampleID || row.sampleId}`}
                        />
                      </td>
                      <td>{row.sampleId}</td>
                      <td>{formatDate(row.requestedDate) || "-"}</td>
                      <td>{formatDate(row.receivedDate) || "-"}</td>
                      <td>{formatDate(row.completeDate) || "-"}</td>
                      <td>{row.barCodeNO || "-"}</td>
                      <td>{row.speciesName || "-"}</td>
                      <td>{row.productTypeName || "-"}</td>
                      <td>{row.lotNumber || "-"}</td>
                      <td>{row.expLevel || "-"}</td>
                      <td>{row.range || "-"}</td>
                      <td>{row.result || "-"}</td>
                      <td>
                        <span className="status-badge">{row.lastStatus || "-"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const handleAdvanceSearch = () => {
    setCurrentPage(1);
    fetchData("", 1, 25);
    setShowAdvancedSearch(false);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData("", 1, 25);
  };

  return (
    <div>
      <div>
        <Container fluid className="second-tab-container">
          <Nav
            variant="underline"
            activeKey="Request List"
            className="flex-nowrap nav-underline"
          >
            {tabConfig.map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link
                  eventKey={tab.key}
                  className="default-font-styles underline-style"
                  active
                >
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}

            <div className="right-aligned-flex-group">
              <Button
                onClick={handleExport}
                variant={
                  selectedRowIds.size > 0 || selectedSampleIds.size > 0
                    ? "active"
                    : "disabled"
                }
                size="sm"
              >
                Export
              </Button>

              <Button
                onClick={handleDownload}
                variant={
                  selectedRowIds.size === 0 && selectedSampleIds.size === 0
                    ? "disabled"
                    : "active"
                }
                size="sm"
              >
                Download
              </Button>

              <div
                className="line-separator"
                style={{
                  width: "1px",
                  height: "46px",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                }}
              />

              <div className="search-input-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="search-input-field"
                />
              </div>
            </div>
          </Nav>
        </Container>

        <PageDescriptionBar items={[
          { type: "icon", icon: "☑️", text: "Select – Check requests or individual samples for bulk actions" },
          { type: "icon", icon: "📤", text: "Export – Export selected request data to a spreadsheet" },
          { type: "icon", icon: "⬇️", text: "Download – Download the lab analysis report PDF" },
          { type: "separator" },
          { type: "icon", icon: "📅", text: "Date Range – Filter requests by submission date" },
        ]} />
        <div className="staff-request-container">
          <div className="filter-section-main">
            <div className="filter-row">
              <div className="date-control-group">
                <label>From</label>
                <div className="date-input-wrapper">
                  <Form.Control
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="date-input-field"
                  />
                </div>
              </div>

              <div className="date-control-group">
                <label>To</label>
                <div className="date-input-wrapper">
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="date-input-field"
                  />
                </div>
              </div>

              <div className="status-control-group">
                <label>Select Status</label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="status-select-field"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <Button variant="outlined" onClick={handleSearch}>
                Search
              </Button>
              <div
                className="line-separator"
                style={{
                  width: "1px",
                  height: "46px",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                }}
              />
              <Button
                variant="outlined"
                onClick={() => setShowAdvancedSearch(true)}
              >
                Advance Search
              </Button>
            </div>
          </div>

          <Card className="table-card">
            <Card.Body className="table-body">
              <SimpleTable
                columns={columns}
                data={requestedListData}
                customRowRender={(row) => (
                  <React.Fragment key={row.groupID}>
                    <tr className="main-table-row">
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedRowIds.has(row.groupID)}
                          onChange={() => toggleRowSelection(row.groupID)}
                        />
                      </td>
                      <td>{row.companyName}</td>
                      <td>{row.locationName}</td>
                      <td>{row.groupID}</td>
                      <td>{row.userName}</td>
                      <td>
                        <div className="d-flex justify-content-center">
                          <div
                            onClick={() => toggleExpand(row.groupID)}
                            className="expand-icon"
                            style={{ cursor: "pointer" }}
                            aria-label={
                              expandedIds.has(row.groupID)
                                ? "Collapse details"
                                : "Expand details"
                            }
                          >
                            <FaChevronDown
                              size={12}
                              style={{
                                transform: expandedIds.has(row.groupID)
                                  ? "rotate(0deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                    {expandedIds.has(row.groupID) && renderExpandedPanel(row)}
                  </React.Fragment>
                )}
                showPagination={true}
                paginationData={paginationData}
                searchValue={searchTerm}
                fetchData={fetchData}
              />
            </Card.Body>
          </Card>
        </div>

        <ReusableModal
          show={showAdvancedSearch}
          onHide={() => setShowAdvancedSearch(false)}
          title="Advanced Search"
          size="lg"
          buttons={[
            {
              text: "Cancel",
              variant: "outlined",
              onClick: () => setShowAdvancedSearch(false),
            },
            {
              text: "Search",
              variant: "active",
              onClick: () => handleAdvanceSearch(),
            },
          ]}
        >
          <Form>
            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Group ID</Form.Label>
                <Form.Control
                  name="groupId"
                  placeholder="Enter Group ID"
                  value={advancedSearch.groupId}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  name="companyName"
                  placeholder="Enter Company Name"
                  value={advancedSearch.companyName}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>User Name</Form.Label>
                <Form.Control
                  name="userName"
                  placeholder="Enter User Name"
                  value={advancedSearch.userName}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Sales Rep</Form.Label>
                <Form.Control
                  name="salesRep"
                  placeholder="Enter Sales Rep"
                  value={advancedSearch.salesRep}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Customer Sample ID</Form.Label>
                <Form.Control
                  name="customerSampleId"
                  placeholder="Enter Customer Sample ID"
                  value={advancedSearch.customerSampleId}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Lot Number</Form.Label>
                <Form.Control
                  name="lotNumber"
                  placeholder="Enter Lot Number"
                  value={advancedSearch.lotNumber}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Sample ID</Form.Label>
                <Form.Control
                  name="sampleId"
                  placeholder="Enter Sample ID"
                  value={advancedSearch.sampleId}
                  onChange={handleAdvancedInputChange}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Product Type</Form.Label>
                <Form.Select
                  name="productType"
                  value={advancedSearch.productType}
                  onChange={handleAdvancedInputChange}
                >
                  {productTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Species Type</Form.Label>
                <Form.Select
                  name="speciesType"
                  value={advancedSearch.speciesType}
                  onChange={handleAdvancedInputChange}
                >
                  {speciesOptions.map((species) => (
                    <option key={species.value} value={species.value}>
                      {species.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>State</Form.Label>
                <Form.Select
                  name="state"
                  value={advancedSearch.state}
                  onChange={handleAdvancedInputChange}
                >
                  {stateOptions.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </Form>
        </ReusableModal>
      </div>
    </div>
  );
};

export default StaffRequestList;
